import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, teamLeaderNavItems, workerNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Search,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Clock,
  Building2,
  ArrowRight,
  CheckCircle2,
  Briefcase,
  Filter,
  AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrowseLiveProjects() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/team/live-projects`);
      setProjects(res.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error(t('browse.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = 
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.location_city?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !typeFilter || project.project_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getComplexityColor = (complexity) => {
    const colors = {
      simple: "bg-green-500/20 text-green-400",
      medium: "bg-yellow-500/20 text-yellow-400",
      complex: "bg-red-500/20 text-red-400"
    };
    return colors[complexity] || "bg-gray-500/20 text-gray-400";
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title={t('browse.availableProjects')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title={t('browse.availableProjects')}>
      <div className="space-y-6" data-testid="browse-live-projects">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{t('browse.availableProjects')}</h1>
            <p className="text-[#94A3B8]">{t('projects.applyToProject')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              {filteredProjects.length} {t('nav.projects')}
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                <Input
                  type="text"
                  placeholder={t('projects.searchProjects')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
                />
              </div>
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-[#94A3B8]" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD] focus:outline-none"
                >
                  <option value="">{t('browse.allTypes')}</option>
                  <option value="assembly">{t('admin.assembly')}</option>
                  <option value="disassembly">{t('admin.disassembly')}</option>
                  <option value="modification">Modifikasjon</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects List */}
        {filteredProjects.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-[#475569]" />
              <h3 className="text-xl font-['Unbounded'] font-bold text-white mb-2">{t('browse.noProjectsAvailable')}</h3>
              <p className="text-[#94A3B8]">
                {searchQuery ? t('clientProjects.tryDifferentSearch') : t('browse.checkBackLater')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredProjects.map((project) => (
              <Card 
                key={project.project_id} 
                className={`bg-[#0A0A0A] border-white/10 hover:border-[#23F7DD]/50 transition-all duration-300 ${project.already_applied ? 'border-green-500/30' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Project Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-['Unbounded'] font-semibold text-white">{project.title}</h3>
                            {project.already_applied && (
                              <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                {t('browse.alreadyApplied')}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-[#94A3B8]">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-[#23F7DD]" />
                              {project.location_city}
                            </span>
                            {project.start_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4 text-[#23F7DD]" />
                                {new Date(project.start_date).toLocaleDateString()}
                              </span>
                            )}
                            {project.crew_size && (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4 text-[#23F7DD]" />
                                {project.crew_size} {t('nav.workers').toLowerCase()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Badge className={`capitalize ${project.project_type === "assembly" ? "bg-[#23F7DD]/20 text-[#23F7DD]" : project.project_type === "disassembly" ? "bg-orange-500/20 text-orange-400" : "bg-purple-500/20 text-purple-400"}`}>
                            {project.project_type}
                          </Badge>
                          <Badge className={getComplexityColor(project.complexity)}>
                            {project.complexity}
                          </Badge>
                        </div>
                      </div>

                      {project.description && (
                        <p className="text-[#94A3B8] text-sm line-clamp-2 mb-3">{project.description}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-[#94A3B8]" />
                          <span className="text-[#94A3B8]">{t('admin.applications')}: </span>
                          <span className="text-white font-medium">{project.applications_count}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#94A3B8]" />
                          <span className="text-[#94A3B8]">{t('common.client')}: </span>
                          <span className="text-white">{project.client_name || "Privat"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Budget & Action */}
                    <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-center gap-4 pt-4 lg:pt-0 border-t lg:border-t-0 lg:border-l border-white/10 lg:pl-6">
                      <div className="text-left lg:text-right">
                        <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('admin.budget')}</p>
                        <p className="text-xl font-['Unbounded'] font-bold text-[#23F7DD]">
                          {project.approved_budget?.toLocaleString()} NOK
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          const basePath = user?.role === "worker" ? "/worker/browse-projects" : "/team-leader/browse-projects";
                          navigate(`${basePath}/${project.project_id}`);
                        }}
                        className={`${project.already_applied ? 'bg-green-500 hover:bg-green-600' : 'bg-[#23F7DD] hover:bg-[#70FFEF]'} text-[#050505] font-bold`}
                      >
                        {project.already_applied ? t('browse.viewDetails') : t('browse.viewDetails')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Box */}
        <Card className="bg-[#23F7DD]/5 border-[#23F7DD]/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#23F7DD] mt-0.5" />
              <div>
                <p className="text-[#23F7DD] font-medium">{t('common.howItWorks')}</p>
                <p className="text-sm text-[#94A3B8] mt-1">
                  {t('browse.howItWorksDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
